/**
 * GenerationReviewDialog — generic review dialog for accepting/rejecting
 * drafts inside a single AI generation run.
 *
 * Parent supplies:
 *   • entity     — 'theme' | 'topic' | 'post'
 *   • run        — the generation run summary
 *   • renderItem — per-draft render slot
 *   • onApply    — fired with ids the user accepted; component does NOT call
 *                  the backend itself (parent owns the HTTP client)
 *
 * Sprint 15 — review-and-apply pattern.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Box, Typography, Alert, Checkbox, Button, Chip, Divider, CircularProgress,
  IconButton, Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import { useGenerationDrafts, type GenerationDraft } from '../../hooks/useGenerationDrafts';
import type { GenerationEntity, GenerationRunSummary } from '../../hooks/useGenerationRuns';

const TESTIDS = {
  dialog: 'generation-review-dialog',
  itemPrefix: 'generation-review-item-',
  selectAll: 'generation-review-select-all',
  applyBtn: 'generation-review-apply',
  cancelBtn: 'generation-review-cancel',
  checkbox: 'generation-review-checkbox-',
} as const;

export interface GenerationReviewDialogProps<TData = Record<string, unknown>> {
  open: boolean;
  onClose: () => void;
  projectId: string;
  entity: GenerationEntity;
  run: GenerationRunSummary | null;
  /** Per-draft renderer — compact view shown inline (always visible). */
  renderItem: (draft: GenerationDraft<TData>, isAccepted: boolean) => React.ReactNode;
  /** Optional — extra content shown ONLY when user expands a row (details view).
      When omitted, the expand chevron is hidden. Use for data-heavy artefacts
      (full DataUsageList, anchor explanations, all fields, etc.) so the
      compact list stays scannable. */
  renderItemDetails?: (draft: GenerationDraft<TData>) => React.ReactNode;
  /** Fired when user clicks Apply. Parent should POST to the right /apply endpoint. */
  onApply: (acceptedItemIds: string[]) => Promise<void> | void;
  /** Optional label for the Apply button */
  applyLabel?: string;
  /** Optional full dialog title for non-content-plan entities that reuse this review/apply shell. */
  title?: string;
  /** Additional dialog title suffix (e.g. "7 themes") */
  titleSuffix?: string;
  /** Allow re-applying an already applied run. Existing canonical records are expected to be skipped by the parent backend. */
  allowApplyAppliedRuns?: boolean;
}

export function GenerationReviewDialog<TData = Record<string, unknown>>({
  open, onClose, projectId, entity, run, renderItem, renderItemDetails, onApply, applyLabel, title, titleSuffix, allowApplyAppliedRuns = false,
}: GenerationReviewDialogProps<TData>): React.ReactElement {
  const { drafts, loading } = useGenerationDrafts<TData>(projectId, entity, run?.id ?? null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const draftSelectionSignature = useMemo(
    () => drafts.map((draft) => `${draft.id}:${draft.accepted === true ? '1' : draft.accepted === false ? '0' : 'n'}`).join('|'),
    [drafts],
  );

  // Reset selection when dialog reopens / run changes / drafts arrive.
  useEffect(() => {
    if (open && run) {
      // Default selection: pre-select drafts where `accepted === true` (re-open an applied run).
      setSelected(new Set(drafts.filter((d) => d.accepted === true).map((d) => d.id)));
    }
    if (!open) {
      setSelected(new Set());
      setExpanded(new Set());
      setError(null);
    }
  }, [draftSelectionSignature, drafts, open, run?.id]);

  const alreadyApplied = run?.status === 'applied';
  const readOnlyApplied = alreadyApplied && !allowApplyAppliedRuns;
  const pendingReview = run?.status === 'completed' || run?.status === 'reviewed';

  const handleToggle = (id: string) => {
    if (readOnlyApplied) return;  // read-only once applied unless caller explicitly allows re-apply
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = drafts.length > 0 && drafts.every((d) => selected.has(d.id));
  const someSelected = drafts.some((d) => selected.has(d.id));
  const handleToggleAll = () => {
    if (readOnlyApplied) return;
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(drafts.map((d) => d.id)));
  };

  const handleApply = async () => {
    setError(null); setSubmitting(true);
    try {
      await onApply(Array.from(selected));
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const total = drafts.length;
  const acceptedCount = selected.size;

  const computedTitle = useMemo(() => {
    const base = title ?? `Review generated ${entity}s`;
    return titleSuffix ? `${base} — ${titleSuffix}` : base;
  }, [entity, title, titleSuffix]);

  return (
    <BaseDialog open={open} onClose={onClose} maxWidth="md" data-testid={TESTIDS.dialog}>
      <DialogHeader
        title={computedTitle}
        subtitle={run
          ? `${run.createdAt?.toLocaleString() ?? ''} · ${run.model ?? 'model?'} · ${total} drafts`
          : ''}
        onClose={onClose}
      />
      <DialogContent>
        <Stack spacing={1.5} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {alreadyApplied && (
            <Alert severity={allowApplyAppliedRuns ? 'info' : 'success'}>
              This run was already applied ({run?.appliedCount ?? 0} items in canonical collection).
              {allowApplyAppliedRuns
                ? ' You can apply selected drafts again; existing canonical items will be skipped.'
                : ' Read-only — to generate more, start a new run.'}
            </Alert>
          )}
          {/* Toolbar — mirrors ScrapeReviewPanel: select-all checkbox + count label + chip */}
          {!readOnlyApplied && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={handleToggleAll}
                size="small"
                data-testid={TESTIDS.selectAll}
              />
              <Typography variant="subtitle2">
                {acceptedCount}/{total} selected
              </Typography>
              {acceptedCount > 0 && (
                <Chip label={acceptedCount} size="small" color="primary" sx={{ fontWeight: 'medium' }} />
              )}
            </Box>
          )}

          <Divider />

          {loading ? (
            <Typography variant="body2" color="text.secondary">Loading drafts…</Typography>
          ) : drafts.length === 0 ? (
            <Alert severity="info">
              {pendingReview
                ? 'No drafts in this run — AI may have rejected input or failed silently.'
                : 'Drafts will appear here as the AI task progresses.'}
            </Alert>
          ) : (
            <Stack spacing={0.5}>
              {drafts.map((draft) => {
                const isAccepted = selected.has(draft.id);
                const isExpanded = expanded.has(draft.id);
                // Reject state uses the explicit `accepted === false` from the doc
                // (after an Apply) — current-session rejected = not-selected drafts
                // in a pending run just render with neutral border.
                const rejectedInDoc = alreadyApplied && draft.accepted === false;
                const hasDetails = Boolean(renderItemDetails);
                return (
                  <Box
                    key={draft.id}
                    data-testid={`${TESTIDS.itemPrefix}${draft.id}`}
                    sx={{
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isAccepted ? 'success.main'
                        : rejectedInDoc ? 'error.main'
                        : 'divider',
                      opacity: rejectedInDoc ? 0.5 : 1,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Box
                      onClick={() => handleToggle(draft.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        cursor: readOnlyApplied ? 'default' : 'pointer',
                        '&:hover': readOnlyApplied ? {} : { bgcolor: 'action.hover' },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isAccepted}
                        disabled={readOnlyApplied}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggle(draft.id)}
                        sx={{ p: 0.5 }}
                        inputProps={{ 'aria-label': `accept draft ${draft.id}` } as any}
                        data-testid={`${TESTIDS.checkbox}${draft.id}`}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {renderItem(draft, isAccepted)}
                      </Box>
                      {hasDetails && (
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleToggleExpand(draft.id); }}
                          sx={{
                            p: 0.5,
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                            transition: 'transform 0.2s',
                          }}
                          data-testid={`generation-review-expand-${draft.id}`}
                          aria-label={isExpanded ? 'Hide details' : 'Show details'}
                        >
                          <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {hasDetails && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 1.5, py: 1, borderTop: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover' }}>
                          {renderItemDetails!(draft)}
                        </Box>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: readOnlyApplied ? 'Close' : 'Cancel',
          onClick: onClose,
          'data-testid': TESTIDS.cancelBtn,
        } as any}
        primaryAction={readOnlyApplied ? undefined : {
          label: submitting ? 'Applying…' : (applyLabel ?? `Apply ${acceptedCount} Selected`),
          onClick: handleApply,
          disabled: submitting || acceptedCount === 0,
          startIcon: submitting ? <CircularProgress size={16} /> : undefined,
          'data-testid': TESTIDS.applyBtn,
        } as any}
      />
    </BaseDialog>
  );
}

export default GenerationReviewDialog;
